import { cloneElement, forwardRef } from "react";

import { cva } from "class-variance-authority";
import { HTMLMotionProps, motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { twMerge } from "tailwind-merge";

type ToastType = "info" | "success" | "warning" | "error";

const ICONS_BY_TYPE: Record<ToastType, React.ReactElement<IconProps>> = {
  info: <Info />,
  success: <CheckCircle2 />,
  warning: <AlertTriangle />,
  error: <XCircle />,
};

const iconVariants = cva("h-5 w-5", {
  variants: {
    type: {
      info: "text-blue-400",
      success: "text-green-400",
      warning: "text-orange-400",
      error: "text-red-400",
    },
  },
});

interface IconProps {
  className?: string;
}

export interface Props
  extends Pick<HTMLMotionProps<"div">, "initial" | "animate" | "exit"> {
  title: string;
  description?: string;
  content?: React.ReactNode;
  type: ToastType;
  icon?: React.ReactElement<IconProps>;
  dismissable?: boolean;
  className?: string;
  classNames?: {
    icon?: string;
  };
  onDismiss: () => void;
}

/**
 * Use to display a notification to the user
 */
const Toast = forwardRef<HTMLDivElement, Props>(
  (
    {
      title,
      description,
      content,
      type,
      icon,
      dismissable = true,
      initial,
      animate,
      exit,
      className,
      onDismiss,
    },
    ref,
  ) => {
    const baseIcon = icon ? icon : ICONS_BY_TYPE[type];
    const styledIcon = cloneElement(baseIcon, {
      className: twMerge(iconVariants({ type }), baseIcon.props.className),
    });

    return (
      <motion.div
        className={twMerge(
          "max-w-md overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5",
          className,
        )}
        initial={initial}
        animate={animate}
        exit={exit}
        ref={ref}
      >
        <div className="p-4">
          <div
            className={twMerge(
              "flex",
              description || content ? "items-start" : "items-center",
            )}
          >
            <div className="flex-shrink-0">{styledIcon}</div>
            <div className="ml-3 mr-1 flex-1">
              <p className="text-sm font-medium text-gray-900">{title}</p>
              <span className="mt-1 text-sm text-gray-500">
                {description && <p className="text-pretty">{description}</p>}
                {content && content}
              </span>
            </div>
            {dismissable && (
              <button
                type="button"
                className="ml-4 inline-flex shrink-0 rounded-md bg-white text-gray-400 hover:text-gray-500"
                onClick={() => {
                  onDismiss();
                }}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  },
);

Toast.displayName = "Toast";

export default Toast;
